import { ApiResponseData, DataWithErrors, skipioAxios } from ".";
import {
  CampaignType,
  MediaMimeType,
} from "../components/CampaignEditor/types";

export type MessageTemplateType =
  | "SmsMessageTemplate"
  | "VoicemailMessageTemplate";

/**
 * Delivery strategy options depend on campaign type
 */
export type DeliveryStrategy =
  | "delay_in_minutes" // drip
  | "delay_in_days" // drip
  | "immediate" // event
  | "minutes_before_event" // event
  | "minutes_after_event" // event
  | "days_before_event" // event
  | "days_after_event"; // event

type MessageTemplateSignal =
  | "neutral"
  | "expression"
  | "sent_to_provider"
  | "negative"
  | "anything"
  | "click"
  | "positive";

export type CampaignCreateRequest = {
  campaign: Required<{
    name: string;
    description: string;
    messageTemplates: (Required<{
      name: string;
      messageTemplateType: MessageTemplateType;
      deliveryStrategy: DeliveryStrategy;
    }> &
      Partial<{
        voicemailId: string | null;
        body: string;
        delayInDays: number;
        delayInMinutes: number;
        daysBeforeEvent: number;
        daysAfterEvent: number;
        minutesBeforeEvent: number;
        minutesAfterEvent: number;
        deliverAtTime: string | null;
        position: number;
        mediaUrls: string[];
        mediaMimeTypes: MediaMimeType[];
        messageTemplateRules: Partial<{
          messageTemplateType: MessageTemplateType;
          signal: MessageTemplateSignal;
          shouldStopCampaign: boolean;
          campaignId: string | null;
          contactGroupId: string | null;
        }>[];
      }>)[];
  }> &
    Partial<{
      isTemplate: boolean;
      isCustom: boolean;
      campaignGroupId: string | null;
      contactFormOnKeyword: boolean;
      removeContactsOnAnyInboundSmsMessage: boolean;
      removeContactsOnInboundPhoneCall: boolean;
      campaignKeywords: {
        keyword: string;
      }[];
    }>;
};

export type CampaignResponse = DataWithErrors<{
  id: string;
  userId: string;
  name: string;
  description: string;
  campaignType: CampaignType;
  status: "active" | "paused";
  statusChangedAt: string | null;
  deletedAt: string | null;
  isTemplate: boolean;
  isCustom: boolean;
  sourceCampaignId: string | null;
  campaignGroupId: string | null;
  campaignGroup?: {
    name: string;
    id: string;
  } | null;
  contactFormOnKeyword: boolean;
  removeContactsOnAnyInboundSmsMessage: boolean;
  removeContactsOnInboundPhoneCall: boolean;
  contactsCount: number;
  participantsCount: number;
  contactIds: string[];
  url: string;
  messageTemplates: DataWithErrors<{
    id: string;
    campaignId: string;
    voicemailId: string | null;
    body: string;
    deliveryStrategy: DeliveryStrategy;
    delayInDays: number;
    delayInMinutes: number;
    daysBeforeEvent: number | null;
    daysAfterEvent: number | null;
    minutesBeforeEvent: number | null;
    minutesAfterEvent: number | null;
    deliverAtTime: string | null;
    messageTemplateType: MessageTemplateType;
    name: string;
    position: number;
    url?: string;
    media?: {
      url: string;
      type: MediaMimeType;
    }[];
    voicemail?: {
      id: string;
      createdAt: string;
      updatedAt: string;
      name: string;
      isStarred: boolean | null;
      audioUrl: string;
      duration: number;
      audioMimeType: string | null;
      audioFileSize: number | null;
      conversionStatus: "in_progress" | "completed" | "failed";
      url: string;
    };
    messageTemplateRules: DataWithErrors<{
      id: string;
      messageTemplateId: string;
      messageTemplateType: MessageTemplateType;
      signal: MessageTemplateSignal;
      shouldStopCampaign: boolean;
      campaignId: string | null;
      contactGroupId: string | null;
      contactAttributeName: string | null;
      expression: string | null;
    }>[];
  }>[];
  campaignKeywords: DataWithErrors<{
    id: string;
    keyword: string;
    campaignId: string;
    userId: string;
  }>[];
}>;

export type CampaignUpdateRequest = {
  campaign: Partial<{
    name: string;
    description: string;
    isTemplate: boolean;
    isCustom: boolean;
    campaignGroupId: string | null;
    contactFormonKeyword: boolean;
    removeContactsOnAnyInboundSmsMessage: boolean;
    removeContactsOnInboundPhoneCall: boolean;
    contactIds: string[];
    campaignKeywords: {
      _destroy?: boolean;
      keyword: string;
      id?: string;
    }[];
    messageTemplates: Partial<{
      _destroy: boolean;
      id: string;
      voicemailId: string | null;
      body: string;
      deliveryStrategy: DeliveryStrategy;
      delayInDays: number;
      delayInMinutes: number;
      daysBeforeEvent: number;
      daysAfterEvent: number;
      minutesBeforeEvent: number;
      minutesAfterEvent: number;
      deliverAtTime: string | null;
      messageTemplateType: MessageTemplateType;
      name: string;
      position: number;
      mediaUrls: string[];
      mediaMimeTypes: MediaMimeType[];
      messageTemplateRules: Partial<{
        _destroy: boolean;
        id: string;
        messageTemplateType: MessageTemplateType;
        signal: MessageTemplateSignal;
        shouldStopCampaign: boolean;
        campaignId: string | null;
        contactGroupId: string | null;
      }>[];
    }>[];
  }>;
};

export const createCampaign = async ({
  campaign,
}: {
  campaign: CampaignCreateRequest["campaign"];
}) => {
  const res = await skipioAxios({
    method: "POST",
    url: "campaigns",
    data: { campaign },
  });
  const resData: ApiResponseData<CampaignResponse> = res.data;

  return resData.data;
};

export const fetchCampaigns = async () => {
  const res = await skipioAxios({
    method: "GET",
    url: `campaigns`,
  });
  const resData: ApiResponseData<CampaignResponse[]> = res.data;

  return resData.data;
};

export const fetchCampaign = async (
  id: string,
  queryParams?: Record<string, unknown>
) => {
  const res = await skipioAxios({
    method: "GET",
    url: `campaigns/${id}`,
    params: queryParams,
  });
  const resData: ApiResponseData<CampaignResponse> = res.data;

  return resData.data;
};

export const updateCampaign = async ({
  id,
  campaign,
}: {
  id: string;
  campaign: CampaignUpdateRequest["campaign"];
}) => {
  const res = await skipioAxios({
    method: "PUT",
    url: `campaigns/${id}`,
    data: { campaign },
  });
  const resData: ApiResponseData<CampaignResponse> = res.data;

  return resData.data;
};

export const deleteCampaign = async ({ id }: { id: string }) => {
  await skipioAxios({
    method: "DELETE",
    url: `campaigns/${id}`,
  });
};
